import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { BookOpen, MagnifyingGlass, FilePdf, Lightbulb, Info } from '@phosphor-icons/react';

// HelpCenter — fetches /user-manual.json (single source of truth also used
// to generate the PDF) and renders a searchable, sidebar-navigated manual.

export default function HelpCenter() {
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch the manual JSON once on mount.
  useEffect(() => {
    let cancelled = false;
    fetch('/user-manual.json')
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) {
          setManual(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Active article from URL hash (#welcome, #picking, …) — default to 1st.
  const activeId = (location.hash || '').replace(/^#/, '') || (manual?.articles?.[0]?.id ?? '');
  const active = manual?.articles.find((a) => a.id === activeId) || manual?.articles?.[0];

  const filtered = useMemo(() => {
    if (!manual) return [];
    const q = query.trim().toLowerCase();
    if (!q) return manual.articles;
    return manual.articles.filter((a) => {
      if (a.title.toLowerCase().includes(q)) return true;
      for (const b of a.blocks || []) {
        if (b.text && b.text.toLowerCase().includes(q)) return true;
        if (b.items && b.items.some((it) => it.toLowerCase().includes(q))) return true;
      }
      return false;
    });
  }, [manual, query]);

  const byCategory = useMemo(() => {
    if (!manual) return {};
    const m = {};
    for (const a of filtered) {
      if (!m[a.category]) m[a.category] = [];
      m[a.category].push(a);
    }
    return m;
  }, [manual, filtered]);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 text-muted-foreground" data-testid="help-loading">Loading user manual…</div>
      </Layout>
    );
  }

  if (!manual) {
    return (
      <Layout>
        <div className="p-6 text-rose-600" data-testid="help-error">
          Could not load /user-manual.json. Please refresh or contact your admin.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col lg:flex-row gap-6 p-4 md:p-6" data-testid="help-center">
        {/* Sidebar */}
        <aside className="w-full lg:w-72 shrink-0 space-y-3 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen size={18} weight="fill" className="text-brand" />
              <h2 className="text-sm font-bold uppercase tracking-widest">{manual.meta.title}</h2>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-3">
              {manual.meta.version}
            </p>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="help-search"
                placeholder="Search the manual…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 rounded-none text-sm"
              />
            </div>
            <a
              href="/Stockaris_User_Manual.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="help-download-pdf"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 border-2 border-foreground bg-foreground text-background hover:bg-foreground/90 text-xs font-bold uppercase tracking-widest rounded-none"
            >
              <FilePdf size={14} weight="fill" /> Download PDF
            </a>
          </div>

          <nav className="pt-3 border-t border-border" data-testid="help-sidebar-nav">
            {manual.categories.map((cat) => {
              const items = byCategory[cat.key];
              if (!items || items.length === 0) return null;
              return (
                <div key={cat.key} className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-1.5">
                    {cat.label}
                  </div>
                  <ul className="space-y-0.5">
                    {items.map((a) => {
                      const isActive = a.id === active?.id;
                      return (
                        <li key={a.id}>
                          <button
                            data-testid={`help-nav-${a.id}`}
                            onClick={() => navigate(`/help#${a.id}`)}
                            className={`w-full text-left text-xs px-2 py-1.5 border-l-2 transition ${
                              isActive
                                ? 'border-brand bg-brand/10 text-brand font-bold'
                                : 'border-transparent hover:border-border hover:bg-muted text-foreground'
                            }`}
                          >
                            {a.title}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground italic py-4" data-testid="help-no-results">
                No articles match "{query}".
              </div>
            )}
          </nav>
        </aside>

        {/* Content */}
        <article className="flex-1 max-w-3xl" data-testid="help-article">
          {active ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand mb-2">
                {manual.categories.find((c) => c.key === active.category)?.label}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="help-article-title">{active.title}</h1>
              {active.audience && active.audience.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-6" data-testid="help-article-audience">
                  {active.audience.map((r) => (
                    <span key={r} className="text-[10px] uppercase tracking-widest font-mono font-bold px-2 py-0.5 bg-muted border border-border">
                      {r}
                    </span>
                  ))}
                </div>
              )}
              <div className="space-y-4">
                {active.blocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
              <div className="mt-12 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono uppercase tracking-widest">
                <span>Article ID: <span className="text-foreground">{active.id}</span></span>
                <Button
                  variant="outline"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="rounded-none"
                  data-testid="help-article-back-to-top"
                >
                  Back to top
                </Button>
              </div>
            </>
          ) : (
            <div className="text-muted-foreground">Select an article to read.</div>
          )}
        </article>
      </div>
    </Layout>
  );
}

// ------------------- block renderer -------------------
function Block({ block }) {
  switch (block.type) {
    case 'h2':
      return <h2 className="text-2xl font-bold mt-4">{block.text}</h2>;
    case 'h3':
      return <h3 className="text-lg font-bold mt-4">{block.text}</h3>;
    case 'h4':
      return <h4 className="text-sm font-bold mt-3 uppercase tracking-wider text-muted-foreground">{block.text}</h4>;
    case 'p':
      return <p className="text-sm text-foreground leading-relaxed">{block.text}</p>;
    case 'ul':
      return (
        <ul className="list-disc pl-5 space-y-1 text-sm text-foreground">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol className="list-decimal pl-5 space-y-1 text-sm text-foreground">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      );
    case 'tip':
      return (
        <div className="flex gap-2 border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-900">
          <Lightbulb size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
          <span><span className="font-bold uppercase text-[10px] tracking-widest mr-1">Tip ·</span>{block.text}</span>
        </div>
      );
    case 'note':
      return (
        <div className="flex gap-2 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
          <Info size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <span><span className="font-bold uppercase text-[10px] tracking-widest mr-1">Note ·</span>{block.text}</span>
        </div>
      );
    default:
      return null;
  }
}
